import React, { Children, FC, FormEvent, ReactNode, useMemo } from 'react';
import { Field as FormField } from 'components/form';
import { ErrorMessage } from '@atlaskit/form';
import { jsx } from '@emotion/react';
import { FieldRenderProps, useField, Field as FinalFormField, FieldProps as FinalFormFieldProps } from 'react-final-form';
import FieldWrapper, { Label, RequiredIndicator } from './style/Field';
import renderComponent from './renderComponent';
// import {Field} from 'formik';

type ChildrenRenderFunctions<FieldValue, T extends HTMLElement = HTMLElement> = (args: FieldRenderProps<FieldValue, T>) => React.ReactNode;

export interface FieldProps<FieldValue, T extends HTMLElement = HTMLElement> extends FinalFormFieldProps<FieldValue, any> {
  name: string;
  /** The name of the field */

  label?: ReactNode;
  /** The label of the field */

  isRequired?: boolean;
  /** Show a * if the field is required */

  component?: React.ComponentType<any>;
  children?: ChildrenRenderFunctions<FieldValue, T> | ReactNode;

  /** Inner ref */
  innerRef?: (instance: any) => void;

  onChange?: (event: React.ChangeEvent<Element> | any) => void;
  // styles?: {
  //   wrapper: CSS;
  // }
  // [otherProp: string]: any;
}

function isInputEvent(e: any): e is React.ChangeEvent<HTMLInputElement> {
  return e.target.value !== undefined;
}

function Field<FieldValue = string, Element extends HTMLElement = HTMLInputElement>(props: FieldProps<FieldValue, Element>) {
  const {
    name,
    label,
    isRequired,
    component,
    children,
    noMargin,
    innerRef,
    onChange,
    ...rest
  } = props;
  const field = useField(name, props);

  const onChangeField = field.input.onChange;

  field.input.onChange = (event: React.ChangeEvent<Element> | any) => {
    if(onChange) {
      onChange(event)
    }
    onChangeField(event);
  };

  const { meta } = field;


  const extendedField = {
    ...field,
    input: {
      ...field.input,
      name,
      isInvalid: Boolean(meta.error),
    },
  };

  const renderField = () => {
    if (typeof children === 'function') {
      return children({ ...extendedField, ...rest })
    }
    const {input, ...fieldRest} = extendedField;
    const componentProps = { ...fieldRest, ...extendedField.input, ...rest };

    if(children) {
      return Children.map(
        (children as ReactNode),
        child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, componentProps)
          }
          return null;
        }
      );
    }

    if(component) {
      return React.createElement(
        component,
        componentProps,
        children
      );
    }

    return null;
  };


  return (
        <FieldWrapper marginTop={!!label}>
          {label && (
            <Label htmlFor={name}>
              {label}
              {isRequired && (
                <RequiredIndicator aria-hidden="true"> * </RequiredIndicator>
              )}
            </Label>
          )}
          {renderField()}
          {meta.touched && meta.error && (
            <ErrorMessage>{meta.error}</ErrorMessage>
          )}
        </FieldWrapper>
  );
};

export default Field;
